import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { buildPipelineAssetProxyUrl, storageKeyFromRenderableUrl } from '../../Review/services/review-media-proxy-url';
import {
  ReviewPostGenerationApiService,
  ReviewSignedUploadTarget,
  ReviewSocialTargetComplete,
  ReviewTemplateRenderUploadPlan,
  ReviewTemplateTargetPrepare
} from '../../Review/services/review-post-generation-api.service';

@Injectable({ providedIn: 'root' })
export class TemplateReviewUploadService {
  private readonly http = inject(HttpClient);
  private readonly reviewPostGeneration = inject(ReviewPostGenerationApiService);

  prepare(postId: string, request: {
    script: string;
    title?: string | null;
    caption?: string | null;
    templateId?: string | null;
    durationSeconds?: number | null;
  }): Promise<ReviewTemplateTargetPrepare> {
    return firstValueFrom(this.reviewPostGeneration.prepareTemplateReviewTarget(postId, request));
  }

  signRenderUpload(postId: string, targetId: string, request: {
    videoContentType?: string | null;
    thumbnailContentType?: string | null;
  }): Promise<ReviewTemplateRenderUploadPlan> {
    return firstValueFrom(this.reviewPostGeneration.signTemplateRenderUpload(postId, targetId, request));
  }

  async uploadRenderedAndComplete(postId: string, plan: ReviewTemplateRenderUploadPlan, video: Blob, thumbnail: Blob | null): Promise<ReviewSocialTargetComplete> {
    await firstValueFrom(this.putTarget(plan.video, video));
    if (thumbnail) {
      await firstValueFrom(this.putTarget(plan.thumbnail, thumbnail));
    }
    return firstValueFrom(this.reviewPostGeneration.completeReviewTarget(postId, plan.targetId));
  }

  private putTarget(target: ReviewSignedUploadTarget, body: Blob) {
    // Route user-scoped R2 keys through the authenticated proxy (avoids direct-R2 CORS / signed-header
    // 403s from the browser) — same approach review-intake uses for client-rendered uploads.
    const key = (target.filePath || '').trim() || storageKeyFromRenderableUrl(target.uploadUrl) || '';
    if (key.startsWith('users/')) {
      const proxyUrl = buildPipelineAssetProxyUrl(environment.apiUrl, key, target.contentType);
      return this.http.put(proxyUrl, body, {
        headers: { 'Content-Type': target.contentType }
      });
    }
    return this.http.put(target.uploadUrl, body, {
      headers: { 'Content-Type': target.contentType }
    });
  }
}
